/**
 * Web check-in statuses ops can set and see, the same for Core, Business4Flyo
 * and ExtPartner. Mirrors core-api `src/types/webcheckin-status.types.ts`
 * (`normalizeAdminStatusInput`): any other status or pairing is refused there.
 */

/** Every status, in the order the editors offer them. */
export const STATUS_OPTIONS = [
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed (hidden from client)' },
  { value: 'FAILED_BY_ADMIN', label: 'Failed by admin (shown to client)' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

/** Short names for chips and badges. */
const STATUS_LABEL = {
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  FAILED_BY_ADMIN: 'Failed by admin',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled'
};

const SUB_STATUS_LABEL = {
  COMPLETE_INFO: 'Complete info',
  MISSING_INFO: 'Missing info',
  BOARDING_PASS_AVAILABLE: 'Boarding pass available',
  BOARDING_PASS_NOT_AVAILABLE: 'Boarding pass not available yet',
  BOARDING_PASS_AT_COUNTER: 'Collect boarding pass at airport counter',
  BOOKING_NOT_FOUND: 'Booking not found',
  AIRLINE_NOT_ALLOWING_WEBCHECKIN: 'Airline not allowing web check-in',
  FLIGHT_ALREADY_DEPARTED: 'Flight already departed',
  DEPARTURE_TOO_CLOSE: 'Departure too close',
  CHECKIN_NOT_SUPPORTED: 'Check-in not supported on this route',
  FLIGHT_CANCELLED: 'Flight cancelled by airline',
  TIMEOUT: 'Bot timed out',
  OTP_REQUIRED: 'OTP required',
  BLOCKED: 'Airline site blocked the bot',
  CAPTCHA_REQUIRED: 'Captcha required',
  SITE_UNAVAILABLE: 'Airline site down',
  PASSPORT_REQUIRED: 'Airline asks for passport',
  TECHNICAL_ERROR: 'Technical error',
  INTERNAL_ERROR: 'Internal error',
  AGENDA_JOB_FAILED: 'Scheduled job failed',
  INVALID_TICKET: 'Booking or flights unusable',
  INVALID_INPUT: 'Could not build the check-in request'
};

/** Sub-statuses that go with each status (empty is always allowed). */
const SUB_STATUSES = {
  SCHEDULED: ['COMPLETE_INFO', 'MISSING_INFO'],
  IN_PROGRESS: [],
  COMPLETED: ['BOARDING_PASS_AVAILABLE', 'BOARDING_PASS_NOT_AVAILABLE', 'BOARDING_PASS_AT_COUNTER'],
  // Internal: what the bot or the airline answered.
  FAILED: [
    'TIMEOUT',
    'OTP_REQUIRED',
    'BLOCKED',
    'CAPTCHA_REQUIRED',
    'SITE_UNAVAILABLE',
    'BOOKING_NOT_FOUND',
    'PASSPORT_REQUIRED',
    'AIRLINE_NOT_ALLOWING_WEBCHECKIN',
    'FLIGHT_CANCELLED',
    'TECHNICAL_ERROR',
    'INTERNAL_ERROR',
    'AGENDA_JOB_FAILED',
    'INVALID_TICKET',
    'INVALID_INPUT'
  ],
  FAILED_BY_ADMIN: ['MISSING_INFO', 'BOOKING_NOT_FOUND', 'AIRLINE_NOT_ALLOWING_WEBCHECKIN'],
  REJECTED: ['FLIGHT_ALREADY_DEPARTED', 'DEPARTURE_TOO_CLOSE', 'CHECKIN_NOT_SUPPORTED'],
  CANCELLED: ['FLIGHT_CANCELLED']
};

/** The API's placeholder for "no value". */
const isEmpty = (code) => !code || code === 'NONE' || code === 'N/A';

const humanize = (code) => {
  const text = String(code).toLowerCase().replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const normalizeStatus = (status) =>
  isEmpty(status) ? '' : String(status).trim().toUpperCase().replace(/-/g, '_');

export const statusLabel = (status) => {
  const code = normalizeStatus(status);
  return code ? STATUS_LABEL[code] || humanize(code) : '—';
};

export const subStatusLabel = (subStatus) =>
  isEmpty(subStatus) ? '—' : SUB_STATUS_LABEL[subStatus] || humanize(subStatus);

export const isSubStatusAllowed = (status, subStatus) =>
  isEmpty(subStatus) || (SUB_STATUSES[normalizeStatus(status)] || []).includes(subStatus);

/** The sub-status to keep when the status changes: the current one if it still fits, else none. */
export const subStatusForStatus = (status, currentSubStatus) =>
  isSubStatusAllowed(status, currentSubStatus) && !isEmpty(currentSubStatus) ? currentSubStatus : '';

/**
 * Options for a select: `allowed` codes (with labels), plus the current value
 * marked "(current, not valid)" when it isn't one of them, so a stored value is
 * always visible instead of the select falling back to its first option.
 */
const withCurrent = (options, current, label) =>
  isEmpty(current) || options.some((option) => option.value === current)
    ? options
    : [...options, { value: current, label: `${label(current)} (current, not valid)`, invalid: true }];

export const statusOptions = (current) => withCurrent(STATUS_OPTIONS, normalizeStatus(current), statusLabel);

export const subStatusOptions = (status, current) =>
  withCurrent(
    (SUB_STATUSES[normalizeStatus(status)] || []).map((value) => ({ value, label: SUB_STATUS_LABEL[value] })),
    isEmpty(current) ? '' : current,
    subStatusLabel
  );

/** Quick fills for the editors. They only fill the fields; ops still save. */
export const CHECK_IN_STATUS_PRESETS = [
  {
    label: 'Boarding pass available',
    status: 'COMPLETED',
    subStatus: 'BOARDING_PASS_AVAILABLE',
    message: 'Boarding pass available'
  },
  {
    label: 'Boarding pass in 4 hours',
    status: 'COMPLETED',
    subStatus: 'BOARDING_PASS_NOT_AVAILABLE',
    message: 'Boarding pass will be available 4 hours before departure.'
  },
  {
    label: 'Collect boarding pass at airport',
    status: 'COMPLETED',
    subStatus: 'BOARDING_PASS_AT_COUNTER',
    message: 'Boarding pass needs to be collected at the airport checkin counter'
  },
  {
    label: 'Booking not found',
    status: 'FAILED_BY_ADMIN',
    subStatus: 'BOOKING_NOT_FOUND',
    message: 'Booking not found on airline website, please checkin at the airport counter'
  },
  {
    label: 'Airline not allowing webcheckin',
    status: 'FAILED_BY_ADMIN',
    subStatus: 'AIRLINE_NOT_ALLOWING_WEBCHECKIN',
    message: 'Airline not allowing webcheckin, please checkin at the airport counter'
  }
];

/**
 * Status cards above each check-in list, in order. `key` is the status code the
 * list is filtered by (Total has none).
 */
export const STATUS_CARDS = [
  { key: 'all', label: 'Total' },
  { key: 'SCHEDULED', label: 'Scheduled' },
  { key: 'IN_PROGRESS', label: 'In progress' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'FAILED', label: 'Failed' },
  { key: 'FAILED_BY_ADMIN', label: 'Failed by admin' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'CANCELLED', label: 'Cancelled' }
];
